import * as React from 'react';
import { getIntrinsicElementProps, slot } from '@fluentui/react-components';
import type { AnimatedPlaceholderProps, AnimatedPlaceholderState } from './AnimatedPlaceholder.types';
import { PlaceholderMotion } from './placeholderMotion';
import { presenceMotionSlot } from '@fluentui/react-motion';

/**
 * Create the state required to render AnimatedPlaceholder.
 *
 * The returned state can be modified with hooks such as useAnimatedPlaceholderStyles_unstable,
 * before being passed to renderAnimatedPlaceholder_unstable.
 *
 * @param props - props from this instance of AnimatedPlaceholder
 * @param ref - reference to root HTMLElement of AnimatedPlaceholder
 */
export const useAnimatedPlaceholder_unstable = (
  props: AnimatedPlaceholderProps,
  ref: React.Ref<HTMLDivElement>,
): AnimatedPlaceholderState => {
  const { placeholderValue } = props;

  const [placeholder, setPlaceholder] = React.useState(placeholderValue);
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    if (placeholderValue !== placeholder) {
      setVisible(false);
    }
  }, [placeholder, placeholderValue]);

  const onMotionFinish = () => {
    setPlaceholder(placeholderValue);
    setVisible(true);
  };

  return {
    components: {
      root: 'div',
      motion: PlaceholderMotion,
      placeholder: 'div',
    },
    root: slot.always(
      getIntrinsicElementProps('div', {
        ref,
        ...props,
      }),
      { elementType: 'div' },
    ),
    motion: presenceMotionSlot<{}>(props.motion, {
      elementType: PlaceholderMotion,
      defaultProps: {
        visible: visible,
        onMotionFinish: onMotionFinish,
      },
    }),
    placeholder: slot.always(
      getIntrinsicElementProps('div', {
        children: placeholder,
      }),
      { elementType: 'div' },
    ),
  };
};
